import styled from "@emotion/styled";
import { ColorProp, getColorFromTheme } from "styles/themes";

import { FeatureUsage } from "lib/hooks/api/usage/useCompanyFeatureUsage";
import { FEATURE_USAGE_CONFIG, formatDaysUsed, formatShare } from "./featureUsageConfig";

type Props = {
  /** Every feature, in the order to list them. */
  features: FeatureUsage[];
};

/**
 * A row per feature: its name, a bar as long as its days used against the most used feature's, then
 * its days used and share in right-aligned columns, as the pie's legend has them.
 */
function FeatureUsageBars({ features }: Props) {
  const mostDaysUsed = Math.max(0, ...features.map((feature) => feature.daysUsed));

  return (
    <Rows aria-label="Feature usage bars">
      {features.map(({ entityType, daysUsed, share }) => {
        const { label, color } = FEATURE_USAGE_CONFIG[entityType];
        return (
          <Row key={entityType}>
            <Label>{label}</Label>
            <Track>
              {daysUsed > 0 && (
                <FeatureBar barColor={color} lengthPercent={(daysUsed / mostDaysUsed) * 100} />
              )}
            </Track>
            <Figure>{formatDaysUsed(daysUsed)}</Figure>
            <Share>{formatShare(share)}</Share>
          </Row>
        );
      })}
    </Rows>
  );
}

const Rows = styled.ul({
  width: "100%",
  maxWidth: 720,
  margin: 0,
  padding: 0,
  listStyle: "none",
});

const Row = styled.li(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "128px minmax(0, 1fr) 88px 48px",
  alignItems: "center",
  gap: 12,
  padding: "10px 0",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const Label = styled.span({
  fontWeight: 500,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const Track = styled.span({
  display: "flex",
  height: 12,
});

/** Square where it starts, rounded at its tip; a sliver even for the least used, so it shows. */
const FeatureBar = styled.span<{ barColor: ColorProp; lengthPercent: number }>(
  ({ theme, barColor, lengthPercent }) => ({
    width: `${lengthPercent}%`,
    minWidth: 2,
    borderRadius: "0 4px 4px 0",
    backgroundColor: getColorFromTheme(barColor, theme),
  }),
);

const Figure = styled.span({
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
});

const Share = styled(Figure)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
}));

export default FeatureUsageBars;
